import type { HeaderType } from '@tempo-io/roadmap-dto';
import { useTranslation } from 'react-i18next';
import type { IPropertySelectorByID } from '@/common/api/types/loopVisualization';
import { useGetCustomFieldDetailsQuery } from '@/common/queries/useGetCustomFieldsQuery';
import {
  getDateHeaderPeriod,
  headerToCustomFieldId,
  headerToItemField,
  isHeaderStartDate,
} from './utils';

export function useHeaderLabel(headerSelector: IPropertySelectorByID | null): string {
  const { t } = useTranslation();

  const { data: field } = useGetCustomFieldDetailsQuery({
    id: headerToCustomFieldId(headerSelector),
  });

  if (!headerSelector) {
    return t(($) => $.loopVisualization.layout.formatPanel.noHeaderSelectedLabel);
  }

  switch (headerSelector.type) {
    case `ParentItemSelector`: {
      return t(($) => $.loopVisualization.layout.headers.parentItemLabel);
    }
    case 'CustomPropertySelectorByID': {
      const itemField: HeaderType | null = headerToItemField(headerSelector);
      if (!itemField) return field?.name || '';
      const headerFieldLabels: Record<HeaderType, string> = {
        status: t(($) => $.loopVisualization.layout.headers.fields.status),
        parentItem: t(($) => $.loopVisualization.layout.headers.fields.parentItem),
        team: t(($) => $.loopVisualization.layout.headers.fields.team),
        itemDate: t(($) => $.loopVisualization.layout.headers.fields.date),
        createdBy: t(($) => $.loopVisualization.layout.headers.fields.createdBy),
        estimationId: t(($) => $.loopVisualization.layout.headers.fields.estimation),
      };
      return headerFieldLabels[itemField] ?? (field?.name || '');
    }
    case 'DateSelector': {
      const labelSuffix = isHeaderStartDate(headerSelector.header)
        ? t(($) => $.loopVisualization.layout.headers.fields.startDate)
        : t(($) => $.loopVisualization.layout.headers.fields.endDate);
      const datePeriod = getDateHeaderPeriod(headerSelector.header);
      const periodLabel = t(
        ($) =>
          $.loopVisualization.layout.headers.fields.period[
            datePeriod as keyof typeof $.loopVisualization.layout.headers.fields.period
          ],
      );
      return `${periodLabel} (${labelSuffix})`;
    }
    case 'createdBy': {
      return t(($) => $.loopVisualization.layout.headers.fields.createdBy);
    }
    default:
      return t(($) => $.loopVisualization.layout.formatPanel.noHeaderSelectedLabel);
  }
}
